/**
 * Choosing properties by sale status, several at once (5 Oct 2026).
 *
 * The Add to mailshot box offered All, or ONE status. To mail Sold STC and Under Offer but
 * not For Sale meant adding twice. These are the tick-boxes' rules: each property falls in
 * exactly one choice, so the counts add up to the whole and "everything ticked" is the same
 * list the old "All" gave.
 */
import { PROPERTY_STATUS } from "../config/propertyStatusMapping";

/** Anything that is not one of the three statuses a scrape saves (Off Market, or none). */
export const OTHER_STATUS = "other";

/** The tick-boxes, in the order they are shown. */
export const STATUS_CHOICES = [
  { value: PROPERTY_STATUS.FOR_SALE, label: "For Sale" },
  { value: PROPERTY_STATUS.UNDER_OFFER, label: "Under Offer" },
  { value: PROPERTY_STATUS.SSTC, label: "SSTC" },
  { value: OTHER_STATUS, label: "Other" },
];

const NAMED = new Set([PROPERTY_STATUS.FOR_SALE, PROPERTY_STATUS.UNDER_OFFER, PROPERTY_STATUS.SSTC]);

/** Which choice a property falls under. */
export const statusChoiceOf = (property) => (NAMED.has(property?.propertyStatus) ? property.propertyStatus : OTHER_STATUS);

/** Every choice ticked: the starting point, and the old "All". */
export const allStatusChoices = () => STATUS_CHOICES.map((c) => c.value);

/** { "For Sale": n, "Under Offer": n, "Sold STC": n, other: n } — always all four keys. */
export function countByStatusChoice(properties = []) {
  const counts = Object.fromEntries(STATUS_CHOICES.map((c) => [c.value, 0]));
  for (const p of properties) counts[statusChoiceOf(p)] += 1;
  return counts;
}

/** The properties whose status is ticked. */
export function filterByStatusChoices(properties = [], selected = []) {
  const chosen = new Set(selected);
  return properties.filter((p) => chosen.has(statusChoiceOf(p)));
}

/** Ticking or unticking one choice, keeping the shown order. */
export function toggleStatusChoice(selected = [], value) {
  const chosen = new Set(selected);
  if (chosen.has(value)) chosen.delete(value);
  else chosen.add(value);
  return STATUS_CHOICES.map((c) => c.value).filter((v) => chosen.has(v));
}

/**
 * Homes with no price, said beside the price limit (the audit of 9 Oct 2026, row 22).
 * The limit leaves out a home priced above it. A home with NO price passes, in the box and in
 * the function alike ("include properties with no price"), and nothing said so. An import's
 * file gives no price at all, so every one of an import's homes passes the limit unseen.
 * @param {Object[]} homes the homes that would be added, after the status, mailing-rule and price choices
 * @param {boolean} limitOn whether the price limit is ticked and has a value
 * @returns {string|null} null when the limit is off or every home has a price
 */
export function noPriceNote(homes, limitOn) {
  if (!limitOn) return null;
  const none = (Array.isArray(homes) ? homes : []).filter((p) => p && !p.price).length;
  if (!none) return null;
  const one = none === 1;
  return `${none.toLocaleString("en-GB")} of these ${one ? "has" : "have"} no price on the record (an import's file gives none), so the price limit does not apply to ${one ? "it" : "them"}: ${one ? "it is" : "they are"} added whatever ${one ? "its" : "their"} price.`;
}
